import { Build, Component, Element, Host, Prop, State, h } from '@stencil/core';
import { assetUrl } from '../../../../../utils/helpers/asset-path';

const COMPONENT_NAME = 'sn-navbar-type-1';

/**
 * Hard ceiling on how long the swap may wait for the navbar's icons to render.
 * Past this the placeholder is swapped out regardless — a late icon is a smaller
 * problem than a navbar that never appears.
 */
const ICON_READY_TIMEOUT_MS = 1000;

/**
 * Navbar component for SignNow brand navigation
 *
 * This navbar is built entirely in the browser: on the server it renders an empty Host
 * (see `render()`), and `sn-navbar-placeholder-type-2` holds the fold with a light static
 * bar plus the SEO link list. `@ssr client-only` is what actually makes that happen — it
 * tells the render pipeline to leave this subtree out of `renderToString`. Without it the
 * empty Host suppressed only this component's own template while its slotted mega-menu
 * (light DOM from the payload) was still fully server-rendered: 78 KB / 9.7 KB gzip ahead
 * of the LCP element, and 29% of the document's SSR nodes.
 *
 * @product signnow
 * @version 2.0.0
 * @category SignNow
 * @ssr client-only
 * @contentConfig Navbar component for site navigation with dropdown menus
 * @slot area-logo - {required}, <me-logo src="..." href="/" alt="..."> - Logo area
 * @slot area-navbar - {required}, {multiple}, <me-button tag="a" variant="ghost" href="..."> - Desktop navigation items (also supports me-dropdown)
 * @slot area-actions - {required}, {multiple}, <me-button variant="primary" href="..."> - Desktop action buttons
 * @slot area-navbar-mobile - {required}, {multiple}, <me-button tag="a" variant="ghost" href="..."> - Mobile navigation items
 * @slot area-actions-mobile - {required}, {multiple}, <me-button variant="secondary" href="..."> - Mobile action buttons
 * @slot area-search - Optional search component override. Uses sn-navbar-search-type-1 by default.
 * @example
 *   <sn-navbar-type-1>
 *     <me-logo slot="area-logo" href="/" src="https://marketing-static.signnow.com/310/images/global/svg/signNow-logo-Colored.svg" alt="airSlate SignNow" width="124" height="42"></me-logo>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="/features">Features</me-button>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="/solutions">Solutions</me-button>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="/integrations">Integrations</me-button>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="https://snseats.signnow.com/pricing">Pricing</me-button>
 *     <me-button slot="area-actions" variant="secondary" href="/contact">Contact sales</me-button>
 *     <me-button slot="area-actions" variant="primary" href="https://snseats.signnow.com/purchase/business_free_trial/loggedout">Free trial</me-button>
 *     <me-button slot="area-navbar-mobile" tag="a" variant="ghost" href="/features">Features</me-button>
 *     <me-button slot="area-navbar-mobile" tag="a" variant="ghost" href="/solutions">Solutions</me-button>
 *     <me-button slot="area-navbar-mobile" tag="a" variant="ghost" href="https://support.signnow.com">Support</me-button>
 *     <me-button slot="area-actions-mobile" variant="secondary" href="/contact">Contact sales</me-button>
 *     <me-button slot="area-actions-mobile" variant="primary" href="https://snseats.signnow.com/purchase/business_free_trial/loggedout">Free trial</me-button>
 *   </sn-navbar-type-1>
 */
@Component({
  tag: 'sn-navbar-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNNavbarType1 {
  @Element() el: HTMLElement;

  /**
   * Color mode for theming
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Tracks if component is ready on client
   */
  @State() isClientReady: boolean = false;

  /**
   * Promise that resolves once the navbar's fetched image assets (e.g. the
   * slotted me-logo, which is a network SVG rather than inline) have been warmed
   * into the browser cache. Started as early as possible so the download runs in
   * parallel with hydration and is ready before the swap.
   */
  private assetsPreloaded?: Promise<void>;

  /** Asset-backed icon names found in the light DOM, collected in `connectedCallback`. */
  private iconNames?: Set<string>;

  connectedCallback() {
    // Runs before the slotted me-buttons load their chunks, which is the point: taking their
    // icons off lazy loading only helps if the flag is there when me-icon reaches
    // componentDidLoad. Doing it in this component's componentDidLoad is too late — the icons
    // have already set up IntersectionObservers that cannot fire under `display: none`.
    // (me-icon's @Watch('lazy') covers the case where a child still wins the race.)
    if (Build.isBrowser) {
      this.iconNames = this.disableIconLazyLoading();
    }
  }

  componentDidLoad() {
    // SSR Overlay Transition Strategy:
    // 1. On SSR: render empty Host with --ssr-deferred class (hidden via CSS)
    // 2. componentDidLoad: set isClientReady → triggers re-render with full navbar + --loading class
    // 3. componentDidUpdate: start transition to visible and hide placeholder
    if (Build.isBrowser) {
      // Kick off image-asset warming immediately (the slotted me-logo lives in
      // this host's light DOM even before the full navbar renders), so the logo
      // image downloads in parallel with hydration instead of only starting when
      // the navbar is revealed.
      this.assetsPreloaded = Promise.all([this.preloadNavbarImageAssets(), this.preloadNavbarIconAssets()]).then(() => undefined);
      this.isClientReady = true;
    }
  }

  componentDidUpdate() {
    if (
      Build.isBrowser &&
      this.isClientReady &&
      this.el.classList.contains('sn-navbar-type-1--loading') &&
      !this.el.classList.contains('sn-navbar-type-1--ready') &&
      !this.el.classList.contains('sn-navbar-type-1--final')
    ) {
      this.transitionToReady();
    }
  }

  private transitionToReady() {
    const innerNavbar = this.el.querySelector('navbar-type-1');
    // Match both placeholder versions so the swap works on legacy pages
    // (sn-navbar-placeholder-type-1) and new pages (sn-navbar-placeholder-type-2).
    const placeholders = document.querySelectorAll('sn-navbar-placeholder-type-1, sn-navbar-placeholder-type-2');

    const iconDeadline = Date.now() + ICON_READY_TIMEOUT_MS;

    const makeReady = () => {
      const dropdowns = this.el.querySelectorAll('me-dropdown');
      const allDropdownsHydrated = Array.from(dropdowns).every(d => d.classList.contains('hydrated'));

      if (!allDropdownsHydrated && dropdowns.length > 0) {
        requestAnimationFrame(() => makeReady());
        return;
      }

      // Gate on the icons having actually rendered, not just on their assets being warm.
      // me-icon paints nothing until its fetch resolves and it re-renders, so swapping the
      // moment the buttons hydrate reveals a bar with empty icon boxes — the placeholder's
      // chevrons and search glyph are inline SVG, so they were visible a frame earlier and
      // the icons read as flickering off and back on.
      if (Date.now() < iconDeadline && !this.areIconsRendered()) {
        requestAnimationFrame(() => makeReady());
        return;
      }

      // Gate the swap on the image assets warmed in componentDidLoad. While the
      // navbar is display:none its me-logo IntersectionObserver never fires, so
      // the logo image would otherwise only start loading once the navbar is
      // revealed — showing the real navbar with a missing/late logo (pop-in).
      // Waiting here means it paints instantly on reveal. Capped by a timeout
      // inside the preload so a slow/failed asset never blocks the transition.
      (this.assetsPreloaded ?? this.preloadNavbarImageAssets()).then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              // Atomic swap: reveal the real navbar and hide the placeholder in
              // the same task so there is no visible gap between them.
              placeholders.forEach(p => p.setAttribute('hidden', ''));
              this.el.classList.remove('sn-navbar-type-1--loading');
              this.el.classList.add('sn-navbar-type-1--ready');
            }, 50);
          });
        });
      });
    };

    if (innerNavbar?.classList.contains('hydrated')) {
      makeReady();
    } else if (innerNavbar) {
      const observer = new MutationObserver(mutations => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (innerNavbar.classList.contains('hydrated')) {
              observer.disconnect();
              makeReady();
              break;
            }
          }
        }
      });
      observer.observe(innerNavbar, { attributes: true, attributeFilter: ['class'] });

      setTimeout(() => {
        observer.disconnect();
        if (!this.el.classList.contains('sn-navbar-type-1--ready') && !this.el.classList.contains('sn-navbar-type-1--final')) {
          makeReady();
        }
      }, 2000);
    }
  }

  /**
   * Warm the browser cache for the navbar's fetched image assets (currently the
   * slotted me-logo image) so they paint instantly when the navbar is revealed.
   * Skips lazy assets inside closed dropdown menus. Resolves when every asset has
   * loaded/errored or after a short safety timeout, whichever comes first.
   */
  private preloadNavbarImageAssets(): Promise<void> {
    if (!Build.isBrowser) return Promise.resolve();

    const urls = new Set<string>();
    this.el.querySelectorAll('me-logo[src]').forEach(el => {
      const src = el.getAttribute('src');
      if (!src) return;
      urls.add(src);
      // me-logo only sets its inner <img src> once its IntersectionObserver
      // fires, which never happens while the navbar is display:none. Set the src
      // eagerly so the image is already present and downloading (images load even
      // inside display:none) well before the reveal — no post-swap pop-in.
      // me-logo re-rendering later with the same URL is a no-op.
      const logoImg = el.querySelector('img');
      if (logoImg && !logoImg.getAttribute('src')) {
        logoImg.setAttribute('src', src);
      }
    });
    this.el.querySelectorAll('img[src]').forEach(el => {
      if ((el as HTMLElement).closest('.me-dropdown__menu')) return;
      const src = el.getAttribute('src');
      if (src) urls.add(src);
    });

    if (urls.size === 0) return Promise.resolve();

    return new Promise<void>(resolve => {
      let pending = urls.size;
      const done = () => {
        if (--pending <= 0) resolve();
      };
      urls.forEach(url => {
        const img = new Image();
        img.onload = done;
        img.onerror = done;
        img.src = url;
      });
      // Safety cap — never block the swap on a slow / failed asset.
      setTimeout(resolve, 800);
    });
  }

  /**
   * Every `me-icon` in the navbar's first-screen area, i.e. excluding the ones inside closed
   * dropdown menus (those are off-screen until the user opens a menu, and gating on them
   * would make the swap wait for the whole mega-menu's icon set).
   */
  private firstScreenIcons(): HTMLElement[] {
    return Array.from(this.el.querySelectorAll<HTMLElement>('me-icon')).filter(el => !el.closest('.me-dropdown__menu'));
  }

  /**
   * True once every first-screen `me-icon` has painted something. An icon that was inlined at
   * SSR (`data-inlined`) renders a `<slot/>` over server markup and is ready immediately; the
   * rest only produce an `<svg>` (or an `<img>`, for URL-named icons) after their fetch lands.
   */
  private areIconsRendered(): boolean {
    return this.firstScreenIcons().every(el => el.hasAttribute('data-inlined') || !!el.querySelector('svg, img'));
  }

  /**
   * Take the first-screen icons off lazy loading, and report which asset names they use.
   *
   * `me-button` defaults `iconLazy` to true, so its `me-icon` waits for an IntersectionObserver —
   * which never fires while the navbar sits at `display: none` behind the placeholder, so the
   * fetch would otherwise only start *after* the swap and the glyph would pop in a frame late.
   * Runs from `connectedCallback` so the flag is set before those children hydrate.
   */
  private disableIconLazyLoading(): Set<string> {
    const names = new Set<string>();

    const collect = (selector: string, nameAttr: string, lazyAttr: string) => {
      this.el.querySelectorAll(selector).forEach(el => {
        if (el.closest('.me-dropdown__menu')) return;
        // Only asset-backed names need warming — inline-SVG strings and absolute URLs are
        // resolved by me-icon without touching /assets/icons (see me-icon.detectIconType).
        const name = el.getAttribute(nameAttr);
        if (!name || name.includes('<svg') || /^https?:\/\//.test(name)) return;
        names.add(name);
        el.setAttribute(lazyAttr, 'false');
      });
    };

    collect('me-button[icon]', 'icon', 'icon-lazy');
    collect('me-icon[name]', 'name', 'lazy');

    // me-dropdown builds its own trigger button in `render()`, so its chevron does not exist
    // in the light DOM yet and the sweep above cannot see it. Warm it by name — those triggers
    // are first-screen on every navbar, and me-dropdown hardcodes this glyph. (It also sets
    // `iconPreload`, so that icon is never lazy in the first place.)
    if (this.el.querySelector('me-dropdown')) {
      names.add('arrows--chevron-down');
    }

    return names;
  }

  /**
   * Prefetch those icon assets so the load runs in parallel with hydration rather than starting
   * when the icons mount.
   *
   * Warmed with `fetch`, not `new Image()`: these assets are cross-origin (CDN), so an image
   * request is `no-cors` and its opaque response is not reusable by me-icon's own cors `fetch`.
   */
  private preloadNavbarIconAssets(): Promise<void> {
    if (!Build.isBrowser || typeof fetch === 'undefined') return Promise.resolve();

    const names = this.iconNames ?? this.disableIconLazyLoading();
    if (names.size === 0) return Promise.resolve();

    // Same `category--name` → `/assets/icons/category/name.svg` mapping me-icon uses
    // (see me-icon.getIconPath — `--` is the directory separator at any depth).
    const urls = Array.from(names, name => assetUrl(`icons/${name.split('--').join('/')}.svg`));

    return new Promise<void>(resolve => {
      let pending = urls.length;
      const done = () => {
        if (--pending <= 0) resolve();
      };
      urls.forEach(url => fetch(url, { cache: 'force-cache' }).then(done, done));
      // Safety cap — never block the swap on a slow / failed asset.
      setTimeout(resolve, 800);
    });
  }

  private setSearchOpened(opened: boolean) {
    this.el.classList.toggle('sn-navbar-type-1--search-opened', opened);
  }

  private handleSearchOpenChange = (event: CustomEvent<{ opened: boolean }>) => {
    this.setSearchOpened(event.detail.opened);
  };

  private handleNavbarClick = (event: MouseEvent) => {
    if (!Build.isBrowser || window.innerWidth <= 960) return;

    const target = event.target as HTMLElement;
    const searchButton = target.closest("me-button[icon='interface--search']");

    if (!searchButton || !this.el.contains(searchButton)) return;

    event.preventDefault();

    const search = this.el.querySelector('sn-navbar-search-type-1') as (HTMLElement & { opened?: boolean }) | null;
    const nextOpened = !search?.opened;

    if (search) {
      search.opened = nextOpened;
    }

    this.setSearchOpened(nextOpened);
  };

  private hasCustomSearchSlot() {
    return Array.from(this.el.children).some(child => child.getAttribute('slot') === 'area-search' && !child.hasAttribute('data-default-navbar-search'));
  }

  render() {
    // SSR: render empty Host — placeholder handles SSR structure
    if (!this.isClientReady) {
      return <Host class="sn-navbar-type-1--ssr-deferred" data-color-mode={this.colorMode}></Host>;
    }

    return (
      <Host class={`${COMPONENT_NAME} ${COMPONENT_NAME}--loading`} data-color-mode={this.colorMode} onSnSearchOpenChange={this.handleSearchOpenChange}>
        <navbar-type-1 class={`${COMPONENT_NAME}--nav`} layout="logo-nav-first" sticky="always" mobileWidth="full" mobileBreakpoint={960} border={false} onClick={this.handleNavbarClick}>
          <slot name="area-logo"></slot>
          <slot name="area-navbar"></slot>
          <slot name="area-actions"></slot>
          {/*
            Below the 960 collapse breakpoint the "Contact sales" (secondary) button is
            swapped for this "Login" ghost link (same target as sn-banner-type-1's Login).
            Hidden on desktop via CSS; the swap/visibility lives in index.scss so the base
            navbar-type-1 stays untouched.
          */}
          <me-button class="sn-navbar-type-1__login-link" slot="area-actions" tag="a" variant="ghost" href="https://app.signnow.com">
            Login
          </me-button>
          <slot name="area-navbar-mobile"></slot>
          <slot name="area-actions-mobile"></slot>
          <slot name="area-search"></slot>
          {!this.hasCustomSearchSlot() && (
            <sn-navbar-search-type-1
              slot="area-search"
              data-default-navbar-search
              colorMode={this.colorMode}
              placeholder="Find anything (ie. integration, solutions, etc.)"
              searchLabel="Search"
              startSearchingLabel="to start searching"
              searchErrorLabel="This field may not be empty."
              suggestions={[
                { label: 'Edit PDFs online', href: 'https://www.signnow.com/features/write-on-pdf-document' },
                { label: 'Send documents for signing', href: 'https://www.signnow.com/features/send-document-for-signing-with-a-click-of-a-button' },
                { label: 'Create reusable templates', href: 'https://www.signnow.com/features/create-templates-of-your-documents' },
                { label: 'Send documents in bulk', href: 'https://www.signnow.com/features/bulk-send-documents-for-electronic-signature' },
                { label: 'All features', href: 'https://www.signnow.com/features' },
              ]}
            ></sn-navbar-search-type-1>
          )}
        </navbar-type-1>
      </Host>
    );
  }
}
